'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { WeatherData } from '@/lib/types';
import { CYRC_INFO } from '@/lib/runwayData';
import { buildPlainLanguageTafTimeline, PlainTafPeriod, RiskLevel, IconName } from '@/lib/tafPlainLanguage';

interface Props {
  weatherData: WeatherData;
}

interface DecodedField {
  key: string;
  label: string;
  value: string | string[];
  icon: React.ReactNode;
  status?: 'ok' | 'caution' | 'alert';
}

function formatWindDir(deg: number): string {
  const dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSO','SO','OSO','O','ONO','NO','NNO'];
  const idx = Math.round(deg / 22.5) % 16;
  return dirs[idx];
}

function formatVisibilityLabel(visKm: number): string {
  if (visKm >= 15) return 'Excellente';
  if (visKm >= 8) return 'Bonne';
  if (visKm >= 5) return 'Réduite';
  return 'Mauvaise';
}

function formatCloudsDecoded(clouds: WeatherData['metar']['clouds'], ceilingLayer: string): string {
  if (!clouds || clouds.length === 0) return 'Ciel dégagé (CLR)';
  const coverageLabel: Record<string, string> = {
    FEW: 'Peu de nuages (FEW)',
    SCT: 'Nuages épars (SCT)',
    BKN: 'Nuages brisés (BKN)',
    OVC: 'Couvert (OVC)',
    VV: 'Visibilité verticale (VV)',
  };
  return clouds.map((c) => `${coverageLabel[c.coverage] ?? c.coverage} à ${c.altitude.toLocaleString('en-US')} pi`).join(' | ');
}

function formatObsTime(timeStr: string): string {
  if (!timeStr) return '—';
  try {
    const d = new Date(timeStr.replace(' ', 'T').replace('Z', '+00:00'));
    if (isNaN(d.getTime())) return timeStr;
    const day = d.getUTCDate();
    const month = d.toLocaleString('fr-FR', { month: 'long', timeZone: 'UTC' });
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const mm = String(d.getUTCMinutes()).padStart(2, '0');
    const localH = (d.getUTCHours() - 4 + 24) % 24;
    const localHH = String(localH).padStart(2, '0');
    return `${day} ${month} à ${hh}:${mm} UTC (${localHH}:${mm} heure locale)`;
  } catch {
    return timeStr;
  }
}

function decodeSlpGroup(group: string): string | null {
  const match = group.match(/^SLP(\d{3})$/);
  if (!match) return null;
  const code = Number(match[1]);
  const pressure = code >= 500 ? 900 + code / 10 : 1000 + code / 10;
  const inHg = (pressure * 0.0295299830714).toFixed(2);
return `Pression au niveau de la mer : ${pressure.toFixed(1)} hPa (${inHg} inHg)`;
}

function decodeDensityAltitude(remarks: string): string | null {
  const match = remarks.match(/\bDENSITY\s+ALT\s+(-?\d+)\s*FT\b/i);
  if (!match) return null;
  const ft = Number(match[1]).toLocaleString('fr-FR');
  return `Altitude-densité : ${ft} ft`;
}

function decodeCloudRemark(group: string): string | null {
  const cloudNames: Record<string, string> = {
    CI: 'cirrus', CS: 'cirrostratus', CC: 'cirrocumulus', AC: 'altocumulus',
    AS: 'altostratus', NS: 'nimbostratus', SC: 'stratocumulus', ST: 'stratus',
    CU: 'cumulus', CB: 'cumulonimbus', TCU: 'cumulus bourgeonnant',
  };
  const parts: string[] = [];
  const regex = /(TCU|CB|CI|CS|CC|AC|AS|NS|SC|ST|CU)(\d)/g;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(group)) !== null) {
    const type = m[1];
    const oktas = m[2];
    const name = cloudNames[type] ?? type;
    parts.push(`${name} ${oktas}/8`);
  }
  return parts.length > 0 ? `Nuages : ${parts.join(', ')}` : null;
}

function decodeWeatherRemark(group: string): string | null {
  const map: Record<string, string> = {
    PRESRR: 'Pression en hausse rapide',
    PRESFR: 'Pression en baisse rapide',
    AO1: 'Station automatique sans détecteur de précipitation',
    AO2: 'Station automatique avec détecteur de précipitation',
    LTG: 'Éclairs observés',
    TS: 'Orage',
    VIRGA: 'Virga observée',
  };
  return map[group] ?? null;
}

const DIR_FR: Record<string, string> = {
  N: 'nord', NE: 'nord-est', E: 'est', SE: 'sud-est',
  S: 'sud', SW: 'sud-ouest', W: 'ouest', NW: 'nord-ouest',
  NNE: 'nord-nord-est', ENE: 'est-nord-est', ESE: 'est-sud-est',
  SSE: 'sud-sud-est', SSW: 'sud-sud-ouest', WSW: 'ouest-sud-ouest',
  WNW: 'ouest-nord-ouest', NNW: 'nord-nord-ouest',
};

function dirFr(d: string): string {
  return DIR_FR[d.toUpperCase()] ?? d.toLowerCase();
}

function decodePcpn(token: string): string | null {
  const map: Record<string, string> = {
    'VRY LGT': 'très faible', LGT: 'faible', MOD: 'modérée', HVY: 'forte',
  };
  for (const [k, v] of Object.entries(map)) {
    if (token.toUpperCase().includes(k)) return `Précipitation : ${v}`;
  }
  return null;
}

function decodeRmk(rawMetar: string): string[] {
  const rmkMatch = rawMetar.match(/\bRMK\b\s*(.*)$/i);
  if (!rmkMatch) return [];
  const remarks = rmkMatch[1].trim();
  if (!remarks) return [];

  const decoded: string[] = [];
  const upper = remarks.toUpperCase();

  // 1. Density altitude (full-string scan)
  const densityAlt = decodeDensityAltitude(remarks);
  if (densityAlt) decoded.push(densityAlt);

  // 2. Cloud groups (e.g. SC3CB2AC2)
  const cloudGroupMatch = remarks.match(/\b([A-Z]{2,3}\d[A-Z]{2,3}\d[A-Z]{2,3}\d|\b(?:TCU|CB|CI|CS|CC|AC|AS|NS|SC|ST|CU)\d\b)/gi);
  if (cloudGroupMatch) {
    for (const grp of cloudGroupMatch) {
      const c = decodeCloudRemark(grp);
      if (c) decoded.push(c);
    }
  }

  // 3. CB direction and movement: "CB SE MOV SE" or "CB NE"
  const cbDirMatch = remarks.match(/\bCB\s+(N|NE|E|SE|S|SW|W|NW|NNE|ENE|ESE|SSE|SSW|WSW|WNW|NNW)(?:\s+MOV\s+(N|NE|E|SE|S|SW|W|NW|NNE|ENE|ESE|SSE|SSW|WSW|WNW|NNW))?/i);
  if (cbDirMatch) {
    const fromDir = dirFr(cbDirMatch[1]);
    if (cbDirMatch[2]) {
      const toDir = dirFr(cbDirMatch[2]);
      decoded.push(`Cumulonimbus : au ${fromDir}, se déplaçant vers le ${toDir}`);
    } else {
      decoded.push(`Cumulonimbus : au ${fromDir}`);
    }
  }

  // 4. SLP group (token-by-token)
  const tokens = upper.split(/\s+/);
  for (const token of tokens) {
    const slp = decodeSlpGroup(token);
    if (slp && !decoded.includes(slp)) decoded.push(slp);
  }

  // 5. Lightning detectors / strikes with distance and direction
  // e.g. "LTNG DTCTRS REP STRIKES 10 NM SE"
  const ltngMatch = remarks.match(/\bLTNG\b.*?(\d+)\s*NM\s+(N|NE|E|SE|S|SW|W|NW|NNE|ENE|ESE|SSE|SSW|WSW|WNW|NNW)/i);
  if (ltngMatch) {
    const dist = ltngMatch[1];
    const dir = dirFr(ltngMatch[2]);
    decoded.push(`Éclairs : détectés / rapportés, impacts à environ ${dist} NM au ${dir}`);
  } else if (/\bLTNG\b/i.test(remarks)) {
    decoded.push('Éclairs : détectés');
  }

  // 6. PCPN (precipitation)
  // e.g. "PCPN VRY LGT" or "PCPN LGT"
  const pcpnMatch = remarks.match(/\bPCPN\s+(VRY\s+LGT|LGT|MOD|HVY)/i);
  if (pcpnMatch) {
    const p = decodePcpn(pcpnMatch[1]);
    if (p) decoded.push(p);
  }

  // 7. Known single-token weather remarks (AO1, AO2, PRESRR, PRESFR, TS, VIRGA)
  for (const token of tokens) {
    const known = decodeWeatherRemark(token);
    if (known && !decoded.includes(known)) decoded.push(known);
  }

  // Return unique items; if nothing decoded, return empty (caller handles fallback)
  return Array.from(new Set(decoded));
}

// ─── Plain-language TAF card helpers ─────────────────────────────────────────

function getRiskLevelStyle(level: RiskLevel): {
  bg: string; border: string; badge: string; badgeText: string; dot: string;
} {
  switch (level) {
    case 'Favorable':
      return { bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-100 text-green-800 border-green-300', badgeText: 'text-green-700', dot: 'bg-green-500' };
    case 'À surveiller':
      return { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-800 border-amber-300', badgeText: 'text-amber-700', dot: 'bg-amber-500' };
    case 'Prudence':
      return { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-800 border-orange-300', badgeText: 'text-orange-700', dot: 'bg-orange-500' };
    case 'Risque accru':
      return { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-800 border-red-300', badgeText: 'text-red-700', dot: 'bg-red-500' };
  }
}

function getBadgeStyle(badge: string): string {
  if (/orage|cumulonimbus/i.test(badge)) return 'bg-purple-100 text-purple-800 border-purple-300';
  if (/verglas|verglaçant/i.test(badge)) return 'bg-red-100 text-red-800 border-red-300';
  if (/neige/i.test(badge)) return 'bg-sky-100 text-sky-800 border-sky-300';
  if (/rafales/i.test(badge)) return 'bg-amber-100 text-amber-800 border-amber-300';
  if (/averses/i.test(badge)) return 'bg-blue-100 text-blue-800 border-blue-300';
  if (/brume|brouillard/i.test(badge)) return 'bg-slate-100 text-slate-700 border-slate-300';
  if (/visibilité réduite/i.test(badge)) return 'bg-orange-100 text-orange-800 border-orange-300';
  if (/nuages bas/i.test(badge)) return 'bg-slate-100 text-slate-700 border-slate-300';
  if (/risque accru/i.test(badge)) return 'bg-red-100 text-red-800 border-red-300';
  if (/prudence/i.test(badge)) return 'bg-orange-100 text-orange-800 border-orange-300';
  if (/surveiller/i.test(badge)) return 'bg-amber-100 text-amber-800 border-amber-300';
  if (/vfr général/i.test(badge)) return 'bg-green-100 text-green-800 border-green-300';
  return 'bg-slate-100 text-slate-700 border-slate-300';
}

function WeatherIconSVG({ icon }: { icon: IconName }) {
  switch (icon) {
    case 'thunderstorm':
      return (
        <svg className="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    case 'rain': case'showers':
      return (
        <svg className="w-7 h-7 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 19l-1 2M12 19l-1 2M16 19l-1 2" />
        </svg>
      );
    case 'snow':
      return (
        <svg className="w-7 h-7 text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v18M3 12h18M5.636 5.636l12.728 12.728M18.364 5.636L5.636 18.364" />
        </svg>
      );
    case 'fog': case'mist':
      return (
        <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 19h16M4 22h10" />
        </svg>
      );
    case 'freezingRain':
      return (
        <svg className="w-7 h-7 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      );
    case 'cloud':
      return (
        <svg className="w-7 h-7 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
        </svg>
      );
    case 'partlyCloudy':
      return (
        <svg className="w-7 h-7 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m12.728 0l-.707-.707M6.343 6.343l-.707-.707" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999" />
        </svg>
      );
    default:
      return (
        <svg className="w-7 h-7 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="5" strokeWidth="2" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
        </svg>
      );
  }
}

function PlainTafCard({ period, index }: { period: PlainTafPeriod; index: number }) {
  const style = getRiskLevelStyle(period.riskLevel);

  return (
    <div className={`rounded-xl border-2 overflow-hidden ${style.bg} ${style.border}`}>
      {/* Header */}
      <div className={`px-4 py-3 border-b ${style.border} flex items-start gap-3`}>
        {/* Icon */}
        <div className="w-10 h-10 rounded-xl bg-white/80 border border-white/60 flex items-center justify-center shrink-0 mt-0.5">
          <WeatherIconSVG icon={period.iconName} />
        </div>
        <div className="flex-1 min-w-0">
          {/* Title: human-readable local time */}
          <div className="text-sm font-700 text-slate-800 leading-snug">{period.title}</div>
          {/* Zulu label */}
          <div className="text-xs text-slate-500 font-500 mt-0.5 font-tabular">{period.zuluLabel}</div>
        </div>
        {/* Risk level badge */}
        <div className={`shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-700 border ${style.badge}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
          {period.riskLevel}
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-2.5">
        {/* Summary */}
        <p className="text-sm font-600 text-slate-700 leading-snug">{period.summary}</p>

        {/* Details grid */}
        <div className="space-y-1.5">
          {period.windText && (
            <div className="flex items-start gap-2 text-sm">
              <span className="text-slate-400 shrink-0">💨</span>
              <span className="text-slate-600"><span className="font-600 text-slate-700">Vent :</span> {period.windText}</span>
            </div>
          )}
          {period.visibilityText && (
            <div className="flex items-start gap-2 text-sm">
              <span className="text-slate-400 shrink-0">👁</span>
              <span className="text-slate-600"><span className="font-600 text-slate-700">Visibilité :</span> {period.visibilityText}</span>
            </div>
          )}
          {period.cloudsText && period.cloudsText !== 'Ciel dégagé' && (
            <div className="flex items-start gap-2 text-sm">
              <span className="text-slate-400 shrink-0">☁️</span>
              <span className="text-slate-600"><span className="font-600 text-slate-700">Nuages :</span> {period.cloudsText}</span>
            </div>
          )}
          {period.weatherText && (
            <div className="flex items-start gap-2 text-sm">
              <span className="text-slate-400 shrink-0">🌧</span>
              <span className="text-slate-600"><span className="font-600 text-slate-700">Météo :</span> {period.weatherText}</span>
            </div>
          )}
        </div>

        {/* TEMPO */}
        {period.temporaryText && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
            <div className="text-xs font-700 text-amber-700 mb-1 uppercase tracking-wide">⚠ Conditions temporaires possibles</div>
            <p className="text-sm text-amber-800 leading-snug">{period.temporaryText}</p>
          </div>
        )}

        {/* PROB */}
        {period.probabilityText && (
          <div className="rounded-lg bg-orange-50 border border-orange-200 px-3 py-2">
            <div className="text-xs font-700 text-orange-700 mb-1 uppercase tracking-wide">⚡ Risque probable</div>
            <p className="text-sm text-orange-800 leading-snug">{period.probabilityText}</p>
          </div>
        )}

        {/* Watch items */}
        {period.watchItems.length > 0 && (
          <div className="flex items-start gap-2 text-sm">
            <span className="text-slate-400 shrink-0">🔍</span>
            <span className="text-slate-600">
              <span className="font-600 text-slate-700">À surveiller :</span> {period.watchItems.join(', ')}
            </span>
          </div>
        )}

        {/* Badges */}
        {period.badges.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {period.badges.map((badge, bi) => (
              <span
                key={bi}
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-700 border ${getBadgeStyle(badge)}`}
              >
                {badge}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function MetarTafContent({ weatherData }: Props) {
  const { metar, taf } = weatherData;
  const [copiedMetar, setCopiedMetar] = useState(false);
  const [copiedTaf, setCopiedTaf] = useState(false);

  const handleCopyMetar = async () => {
    await navigator.clipboard.writeText(metar.raw);
    setCopiedMetar(true);
    toast.success('METAR copié dans le presse-papiers');
    setTimeout(() => setCopiedMetar(false), 2000);
  };

  const handleCopyTaf = async () => {
    await navigator.clipboard.writeText(taf.raw);
    setCopiedTaf(true);
    toast.success('TAF copié dans le presse-papiers');
    setTimeout(() => setCopiedTaf(false), 2000);
  };

  const stationName = weatherData.station === 'CYBG' ?'CYBG — Bagotville, QC (secours)' :'CYRC — Chicoutimi / Saint-Honoré, QC';

  const windDecoded = metar.windSpeed === 0
    ? 'Calme (0 kt)'
    : `Vent du ${metar.windDirection}° (${formatWindDir(metar.windDirection)}) à ${metar.windSpeed} nœuds${metar.gustSpeed > 0 ? `, rafales à ${metar.gustSpeed} nœuds` : ''}`;

  const visibilityDecoded = `Visibilité ${metar.visibility} km — ${formatVisibilityLabel(metar.visibility)}`;
  const cloudsDecoded = formatCloudsDecoded(metar.clouds, metar.ceilingLayer);
  const tempDecoded = `Température ${metar.temperature}°C / Point de rosée ${metar.dewpoint}°C`;
  const altInHg = (metar.pressure / 33.8639).toFixed(2);
  const pressureTrendLabel = weatherData.supplemental.pressureTrend === 'rising' ? 'Hausse'
    : weatherData.supplemental.pressureTrend === 'falling' ? 'Baisse' : 'Stable';
  const pressureDecoded = `Altimètre ${altInHg} inHg (${metar.pressure} hPa) — ${pressureTrendLabel}`;
  const remarksDecoded = decodeRmk(metar.raw);
  const flightCategoryDecoded = metar.flightCategory === 'VFR' ? 'VFR — Conditions de vol à vue'
    : metar.flightCategory === 'MVFR' ? 'MVFR — Conditions visuelles marginales'
    : metar.flightCategory === 'IFR' ? 'IFR — Conditions aux instruments' : 'LIFR — Conditions aux instruments basses';

  const decodedMetarFields: DecodedField[] = [
    {
      key: 'dec-station', label: 'Station', value: stationName,
      icon: <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>,
    },
    {
      key: 'dec-time', label: "Heure d'observation", value: formatObsTime(metar.time),
      icon: <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="2" /><path strokeLinecap="round" d="M12 6v6l4 2" strokeWidth="2" /></svg>,
    },
    {
      key: 'dec-wind', label: 'Vent', value: windDecoded, status: 'ok',
      icon: <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" /></svg>,
    },
    {
      key: 'dec-visibility', label: 'Visibilité', value: visibilityDecoded, status: 'ok',
      icon: <svg className="w-4 h-4 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>,
    },
    {
      key: 'dec-clouds', label: 'Nuages', value: cloudsDecoded, status: 'ok',
      icon: <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" /></svg>,
    },
    {
      key: 'dec-temp', label: 'Température / Point de rosée', value: tempDecoded, status: 'ok',
      icon: <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" /></svg>,
    },
    {
      key: 'dec-pressure', label: 'Pression altimétrique', value: pressureDecoded, status: 'ok',
      icon: <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="2" /><path strokeLinecap="round" d="M12 6v6l4 2" strokeWidth="2" /></svg>,
    },
    {
      key: 'dec-remarks', label: 'Remarques (RMK)', value: remarksDecoded, status: 'ok',
      icon: <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
    },
    {
      key: 'dec-category', label: 'Catégorie de vol', value: flightCategoryDecoded, status: 'ok',
      icon: <svg className="w-4 h-4 text-vfr" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
    },
  ];

  // Build plain-language TAF timeline
  const tafPeriods = taf.raw ? buildPlainLanguageTafTimeline(taf.raw) : [];

  return (
    <div
      className="page-scroll px-3 py-3 space-y-4"
      style={{ overflowX: 'hidden', overflowY: 'auto', height: '100%', paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)' }}
    >
      {/* Station info banner */}
      <div className="card-aviation-blue p-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
            </div>
            <div>
              <div className="text-lg font-800 text-foreground">{CYRC_INFO.icao}</div>
              <div className="text-sm text-muted-foreground">{CYRC_INFO.name}, {CYRC_INFO.province}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 text-sm items-center">
            <div className="flex flex-col">
              <span className="metric-label">COORDONNÉES</span>
              <span className="font-600 text-foreground font-tabular">{CYRC_INFO.lat}°N, {Math.abs(CYRC_INFO.lon)}°O</span>
            </div>
            <div className="flex flex-col">
              <span className="metric-label">ÉLÉVATION</span>
              <span className="font-600 text-foreground font-tabular">{CYRC_INFO.elevation} pi MSL</span>
            </div>
            <div className="flex flex-col">
              <span className="metric-label">SOURCE DONNÉES</span>
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-700 border ${
                  weatherData.dataSource === 'CYRC' ? 'bg-green-100 text-green-700 border-green-300'
                    : weatherData.dataSource === 'CYBG' ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-slate-100 text-slate-500 border-slate-300'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    weatherData.dataSource === 'CYRC' ? 'bg-green-500' :
                    weatherData.dataSource === 'CYBG' ? 'bg-amber-500' : 'bg-slate-400'
                  }`} />
                  {weatherData.dataSource}
                  {weatherData.dataSource === 'CYBG' && ' ⚡ secours'}
                </span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-600 border ${
                  metar.ageMinutes > 90 ? 'bg-red-100 text-red-700 border-red-300'
                    : metar.ageMinutes > 60 ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-blue-50 text-blue-600 border-blue-200'
                }`}>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" strokeWidth="2" />
                    <path strokeLinecap="round" d="M12 6v6l4 2" strokeWidth="2" />
                  </svg>
                  {metar.ageMinutes} min
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CYBG fallback warning */}
      {weatherData.dataSource === 'CYBG' && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-300">
          <svg className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <div className="text-sm font-700 text-amber-800">Source météo de secours — CYBG / Bagotville</div>
            <div className="text-xs text-amber-700 mt-0.5">CYRC (Chicoutimi / Saint-Honoré) n&apos;a retourné aucune donnée. Les données affichées proviennent de CYBG (Bagotville), station proche.</div>
          </div>
        </div>
      )}

      {/* METAR section */}
      <div className="card-aviation p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-700 text-foreground">METAR</h3>
              <p className="text-xs text-muted-foreground">{weatherData.station} · {metar.time}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-600 px-2 py-0.5 rounded-full ${
              metar.ageMinutes > 90 ? 'bg-amber-100 text-amber-700 border border-amber-200' : 'bg-green-100 text-vfr border border-green-200'
            }`}>
              {metar.ageMinutes > 90 ? '⚠️ ' : '✓ '}Il y a {metar.ageMinutes} min
            </span>
            <button
              onClick={handleCopyMetar}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-secondary text-foreground text-xs font-600 hover:bg-primary hover:text-white transition-all duration-150 active:scale-95"
            >
              {copiedMetar ? (
                <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>Copié!</>
              ) : (
                <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>Copier</>
              )}
            </button>
          </div>
        </div>

        {/* Raw METAR */}
        <div className="bg-slate-900 rounded-xl p-3 font-mono text-sm text-green-400 leading-relaxed break-all mb-4 select-all" style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}>
          {metar.raw || 'METAR non disponible'}
        </div>

        {/* Decoded METAR */}
        <div>
          <div className="text-sm font-700 text-foreground mb-2 flex items-center gap-1.5">
            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            METAR décodé
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {decodedMetarFields.map((field) => (
              <div key={field.key} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50 border border-border/50">
                <div className="w-7 h-7 rounded-lg bg-white flex items-center justify-center shrink-0 shadow-sm">
                  {field.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="metric-label mb-0.5">{field.label}</div>
                  {field.key === 'dec-remarks' ? (
                    Array.isArray(field.value) && field.value.length > 0 ? (
                      <ul className="space-y-0.5">
                        {(field.value as string[]).map((item, i) => (
                          <li key={i} className="text-sm font-600 text-foreground leading-snug">{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-sm font-600 text-foreground leading-snug italic opacity-70">
                        Remarque non décodée disponible dans le METAR brut.
                      </div>
                    )
                  ) : (
                    <div className="text-sm font-600 text-foreground leading-snug break-words">{field.value as string}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* TAF section */}
      <div className="card-aviation p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-700 text-foreground">TAF</h3>
              <p className="text-xs text-muted-foreground">{weatherData.station} · Émis {taf.issuedAt || '—'} · Prévision 30h</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-600 px-2 py-0.5 rounded-full bg-blue-100 text-primary border border-blue-200">Valide 30h</span>
          </div>
        </div>

        {/* ── SECTION 1 : TAF BRUT ── */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-sm font-700 text-foreground flex items-center gap-1.5">
              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              TAF brut
            </div>
            <button
              onClick={handleCopyTaf}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-secondary text-foreground text-xs font-600 hover:bg-primary hover:text-white transition-all duration-150 active:scale-95"
            >
              {copiedTaf ? (
                <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>Copié!</>
              ) : (
                <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>Copier</>
              )}
            </button>
          </div>
          <div
            className="bg-slate-900 rounded-xl p-3 font-mono text-sm text-cyan-400 leading-relaxed select-all"
            style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
          >
            {taf.raw || 'TAF non disponible pour cette station'}
          </div>
        </div>

        {/* ── SECTION 2 : PRÉVISION DÉCODÉE EN CLAIR ── */}
        <div>
          <div className="mb-3">
            <div className="text-sm font-700 text-foreground flex items-center gap-1.5 mb-1">
              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Prévision décodée en clair
            </div>
            <p className="text-xs text-muted-foreground">
              Lecture simplifiée du TAF. Toujours valider avec les sources officielles.
            </p>
          </div>

          {tafPeriods.length > 0 ? (
            <div className="space-y-3">
              {tafPeriods.map((period, idx) => (
                <PlainTafCard key={period.id} period={period} index={idx} />
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground italic p-3 rounded-lg bg-secondary/50 border border-border/50">
              {taf.raw ? 'Décodage TAF non disponible.' : 'TAF non disponible pour cette station'}
            </div>
          )}
        </div>
      </div>

      {/* Data quality footer */}
      <div className="card-aviation p-3 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <div className="pulse-dot w-2 h-2 rounded-full bg-vfr" />
          <span className="text-xs text-muted-foreground">
            Source: <span className="font-600 text-foreground">{weatherData.dataSource}</span>
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" strokeWidth="2" />
            <path strokeLinecap="round" d="M12 6v6l4 2" strokeWidth="2" />
          </svg>
          <span className="text-xs text-muted-foreground">
            METAR âgé de <span className="font-600 text-foreground">{metar.ageMinutes} min</span>
            {metar.ageMinutes > 60 && <span className="text-caution"> — données à vérifier</span>}
          </span>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-xs text-muted-foreground">Données via AviationWeather.gov — NOAA/FAA</span>
        </div>
      </div>
    </div>
  );
}